/**
 * The open job-change question about this contact, on the person sheet (SC-13586).
 *
 * Also the owner of the review dialog for the whole sheet: a bell CTA lands on
 * `?personSheet=<id>&suggestion=<id>`, and this component opens the dialog from that param.
 *
 * PersonDetailSheet is always mounted, so the banner's query is gated on the sheet being OPEN
 * and the feature flag — never on the contact id alone. The deep link deliberately ignores the
 * flag: a notification only exists for a workspace that opted in, and a CTA that opened nothing
 * would be a dead end.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Briefcase } from "lucide-react";

import { Button } from "@/components/ui/button";
import JobChangeReviewDialog from "@/components/shared/JobChangeReviewDialog";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import { usePendingContactChangeSuggestion } from "@/lib/hooks/use-contact-change-suggestion";

interface Props {
  contactId: string | null | undefined;
  sheetOpen: boolean;
}

export default function JobChangeSuggestionBanner({ contactId, sheetOpen }: Props) {
  const { t } = useTranslation();
  const enabled = useFeatureFlag(FEATURE_FLAGS.CONTACT_JOB_CHANGE_SUGGESTIONS);
  const [searchParams, setSearchParams] = useSearchParams();
  // Only the sheet showing the linked person answers the link: nested person sheets render this
  // component too, and two dialogs for one click would be the result otherwise.
  const linked = sheetOpen && contactId && searchParams.get("personSheet") === contactId
    ? searchParams.get("suggestion")
    : null;

  const { suggestion, refetch } = usePendingContactChangeSuggestion(contactId, sheetOpen && enabled);
  const [manualId, setManualId] = useState<string | null>(null);

  const dialogId = linked ?? manualId;

  const close = (open: boolean) => {
    if (open) return;
    setManualId(null);
    if (linked) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("suggestion");
        return next;
      }, { replace: true });
    }
  };

  const snap = suggestion?.snapshot ?? {};
  const text = suggestion?.kind === "TITLE_CHANGED"
    ? t("detailSheets.jobChange.banner.title", { personName: snap.personName, toTitle: snap.toTitle })
    : t("detailSheets.jobChange.banner.company", { personName: snap.personName, toCompany: snap.toCompany });

  return (
    <>
      {suggestion && !suggestion.dismissedByViewer && (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2">
          <Briefcase className="h-4 w-4 text-warning shrink-0" />
          <p className="text-sm flex-1 min-w-0">{text}</p>
          <Button size="sm" variant="outline" onClick={() => setManualId(suggestion.id)}>
            {t("detailSheets.jobChange.banner.review")}
          </Button>
        </div>
      )}
      <JobChangeReviewDialog
        suggestionId={dialogId}
        open={Boolean(dialogId)}
        onOpenChange={close}
        onResolved={() => void refetch()}
      />
    </>
  );
}
